import { useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './useGuide.css';
import { getLoggedUserGPID } from 'utils';
import { GUIDE_TOUR_CATALOG_STEP_MAP } from 'utils/guidedTourCatalogs';

const useGuidedTour = () => {
    // Persistent driver instance using useRef
    const driverRef = useRef();
    if (!driverRef.current) {
        driverRef.current = driver({ showProgress: true, allowHTML: true, allowClose: false });
    }
    // Fetch steps from JSON files for '/' and '/services' paths
    const getStepsForPath = async (pathname, search = '') => {
        const fetchSteps = async (jsonFile, serviceType = 'aws') => {
            const basePath = `/static/api/guide-tour/${serviceType}`;
            const response = await fetch(`${basePath}/${jsonFile}`);
            return response.ok ? response.json() : [];
        };

        if (pathname === '/') {
            return fetchSteps('landing-page.json', 'aws');
        }
        if (pathname === '/services') {
            const params = new URLSearchParams(search);
            const service = params.get('services');
            if (service === 'aws') {
                return fetchSteps('services.json', 'aws');
            }
            if (service === 'azure') {
                return fetchSteps('services.json', 'azure');
            }
            return [];
        }

        const match = GUIDE_TOUR_CATALOG_STEP_MAP?.find(({ ids }) => ids?.some((id) => pathname?.includes(id)));
        if (match) {
            return fetchSteps(match?.json, match?.serviceType);
        }

        return [];
    };

    // Persist tour state in sessionStorage to continue across navigation
    const TOUR_KEY = 'devx-guided-tour-active';
    const FIRST_TIME_KEY_PREFIX = 'devx-first-time-user';
    const STARTED_KEY_PREFIX = 'devx-guided-tour-started';

    const getFirstTimeKey = () => {
        try {
            const gpid = getLoggedUserGPID();
            return `${FIRST_TIME_KEY_PREFIX}:${gpid || 'anonymous'}`;
        } catch (e) {
            return `${FIRST_TIME_KEY_PREFIX}:anonymous`;
        }
    };

    const getStartedKey = () => {
        try {
            const gpid = getLoggedUserGPID();
            return `${STARTED_KEY_PREFIX}:${gpid || 'anonymous'}`;
        } catch (e) {
            return `${STARTED_KEY_PREFIX}:anonymous`;
        }
    };

    const isFirstTimeUser = () => {
        try {
            return localStorage.getItem(getFirstTimeKey()) === null;
        } catch (e) {
            return false;
        }
    };

    const markTourStartedForUser = () => {
        try {
            localStorage.setItem(getStartedKey(), 'true');
        } catch (e) {
            // ignore storage errors
        }
    };

    const markUserKnown = () => {
        try {
            localStorage.setItem(getFirstTimeKey(), 'false');
            localStorage.removeItem(getStartedKey());
        } catch (e) {
            // ignore storage errors
        }
    };

    // Helpers to resolve selector to element and check visibility
    const resolveElement = (selectorOrElement) => {
        if (!selectorOrElement) return null;
        if (typeof selectorOrElement === 'string') {
            try {
                return document.querySelector(selectorOrElement);
            } catch (e) {
                return null;
            }
        }
        return selectorOrElement?.nodeType === 1 ? selectorOrElement : null;
    };

    const isElementVisible = (el) => {
        if (!el) return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
        const rect = el.getBoundingClientRect();
        return !!(rect.width || rect.height);
    };

    const filterVisibleSteps = (steps = []) => steps.filter((step) => isElementVisible(resolveElement(step?.element)));

    // Start the tour for the current path and set session flag
    // Accepts both pathname and search
    const startTour = async (pathname, search = '') => {
        sessionStorage.setItem(TOUR_KEY, 'true');
        markTourStartedForUser();
        const steps = await getStepsForPath(pathname, search);
        const visibleSteps = filterVisibleSteps(steps);
        if (visibleSteps && visibleSteps.length > 0) {
            driverRef.current.setSteps(visibleSteps);
            driverRef.current.drive();
        }
    };

    // Call this on route change to auto-continue the tour if active
    // Accepts both pathname and search
    const continueTourIfActive = async (pathname, search = '') => {
        if (sessionStorage.getItem(TOUR_KEY) === 'true') {
            const steps = await getStepsForPath(pathname, search);
            const visibleSteps = filterVisibleSteps(steps);
            if (visibleSteps && visibleSteps.length > 0) {
                // Ensure any previous tour state is cleared before starting anew
                if (driverRef.current && typeof driverRef.current.destroy === 'function') {
                    driverRef.current.destroy();
                }
                driverRef.current.setSteps(visibleSteps);
                driverRef.current.drive();
            }
        }
    };

    // Call this to stop the tour and clear the session flag
    const stopTour = () => {
        sessionStorage.removeItem(TOUR_KEY);
        if (driverRef.current && typeof driverRef.current.destroy === 'function') {
            driverRef.current.destroy();
        }
        try {
            if (localStorage.getItem(getStartedKey()) === 'true') {
                markUserKnown();
            }
        } catch (e) {
            // ignore storage errors
        }
    };

    // Highlight a specific element
    const highlightElement = (elementId, title, description) => {
        driverRef.current.highlight({
            element: elementId,
            popover: {
                title,
                description,
            },
        });
    };

    return {
        startTour, // call with current path, e.g. startTour(location.pathname)
        continueTourIfActive, // call on route change with current path
        stopTour,
        highlightElement,
        isFirstTimeUser,
    };
};

export default useGuidedTour;
